import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Ban, Check, Mail, Reply, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
  Confidences,
  ReplyClasses,
  formatDateTime,
  messages,
  type InboundMessageDetail,
  type MailerStatus,
} from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input, Select, Textarea } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/common/status-badge';
import { ConfirmDialog } from '@/components/common/feedback';
import { BottomActions, PageHeader } from '@/components/common/page';
import { AppointmentDialog } from '@/features/inbox/appointment-dialog';
import { MailHtmlBody } from '@/features/mail/mail-body';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/** S51 受信詳細 */
export const InboxDetailPage = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [replyOpen, setReplyOpen] = useState(false);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [appointmentOpen, setAppointmentOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [classifyOpen, setClassifyOpen] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [showImages, setShowImages] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: qk.inboxItem(id),
    queryFn: () => api.get<SingleResponse<InboundMessageDetail>>(`/inbox/${id}`),
  });

  const draft = useMutation({
    mutationFn: () =>
      api.post<SingleResponse<{ subject: string; body: string }>>(`/inbox/${id}/reply-draft`, {
        tone: 'polite',
      }),
    onSuccess: (result) => {
      setReplySubject(result.data.subject);
      setReplyBody(result.data.body);
      toast.success('下書きを作成しました', { description: '内容を確認してから送信してください' });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const reply = useMutation({
    mutationFn: () =>
      api.post(`/inbox/${id}/reply`, { subject: replySubject, body: replyBody, markDone: true }),
    onSuccess: () => {
      toast.success('返信を送信しました');
      void queryClient.invalidateQueries({ queryKey: qk.inboxItem(id) });
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
      setReplyOpen(false);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  // Google Workspace を接続していれば、同じメールを Gmail のスレッドとして開ける
  const mailer = useQuery({
    queryKey: qk.mailerStatus,
    queryFn: () => api.get<SingleResponse<MailerStatus>>('/mailer/status'),
  });
  const openInMailer = useMutation({
    mutationFn: () => api.get<SingleResponse<{ threadId: string | null }>>('/mailer/lookup', { inboundId: id }),
    onSuccess: (result) => {
      if (result.data.threadId) navigate(`/mail/${result.data.threadId}`);
      else toast.error('接続した受信箱にこのメールが見つかりませんでした');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const markDone = useMutation({
    mutationFn: () => api.patch(`/inbox/${id}`, { handlingStatus: 'done' }),
    onSuccess: () => {
      toast.success('対応を完了にしました');
      void queryClient.invalidateQueries({ queryKey: qk.inboxItem(id) });
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (isLoading || !data) return <SkeletonRows rows={6} />;
  const item = data.data;

  return (
    <>
      <PageHeader
        back="/inbox"
        title={item.companyName ?? item.fromEmail}
        badge={
          item.replyClass ? (
            <StatusBadge
              kind="reply_class"
              value={item.replyClass}
              detail={
                item.classConfidence != null
                  ? `確度 ${Confidences.label(item.classConfidence >= 0.8 ? 'high' : item.classConfidence >= 0.6 ? 'medium' : 'low')}`
                  : undefined
              }
              size="lg"
            />
          ) : undefined
        }
        actions={
          <>
            {mailer.data?.data.connected && (
              <Button variant="outline" onClick={() => openInMailer.mutate()} disabled={openInMailer.isPending}>
                <Mail aria-hidden />
                メーラーで開く
              </Button>
            )}
            <Button variant="outline" onClick={() => setClassifyOpen(true)}>
              {messages.inbox.changeClass}
            </Button>
            {!item.companyId && (
              <Button onClick={() => setLinkOpen(true)}>{messages.inbox.linkCompany}</Button>
            )}
          </>
        }
      />

      {/* 配信停止の場合は明示する */}
      {item.isSuppressed && (
        <p className="mb-3 flex items-start gap-2 rounded-card border-2 border-foreground bg-muted p-3 text-body font-semibold">
          <Ban aria-hidden className="mt-0.5 size-5 shrink-0" />
          {messages.inbox.unsubscribedNotice}
        </p>
      )}

      {!item.companyId && (
        <Card className="mb-3 border-2 border-foreground">
          <CardContent className="space-y-2 pt-4">
            <p className="flex items-center gap-1.5 text-body font-semibold">
              <AlertTriangle aria-hidden className="size-5" />
              {messages.inbox.unlinked}
            </p>
            <p className="text-body text-foreground-muted">{item.fromEmail}</p>
            {item.matchCandidates.length > 0 && (
              <ul className="space-y-1">
                {item.matchCandidates.map((candidate) => (
                  <li key={candidate.companyId} className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-body">{candidate.companyName}</span>
                    <span className="text-note text-foreground-muted">{candidate.reason}</span>
                    <LinkButton inboundId={id} companyId={candidate.companyId} />
                  </li>
                ))}
              </ul>
            )}
            <Button variant="outline" size="sm" onClick={() => setLinkOpen(true)}>
              {messages.inbox.linkCompany}
            </Button>
          </CardContent>
        </Card>
      )}

      {item.classRationale && (
        <p className="mb-3 rounded-control border border-border bg-surface px-3 py-2 text-note text-foreground-muted">
          {messages.inbox.classEvidence}：「{item.classRationale}」
        </p>
      )}

      {item.companyId && (
        <Card className="mb-3">
          <CardContent className="flex flex-wrap items-center gap-2 pt-4">
            <span className="text-body">企業: </span>
            <Button variant="link" onClick={() => navigate(`/companies/${item.companyId}`)}>
              {item.companyName}
            </Button>
            {item.companyStatus && (
              <StatusBadge kind="company_status" value={item.companyStatus} />
            )}
          </CardContent>
        </Card>
      )}

      {item.sendAttempt && (
        <details className="mb-3 rounded-card border border-border p-3">
          <summary className="cursor-pointer text-body font-semibold">
            {messages.inbox.sentMessageLink}（{formatDateTime(item.sendAttempt.sentAt)}）
          </summary>
          <p className="mt-2 text-body">{item.sendAttempt.subject}</p>
          {item.sendAttempt.generatedMessageId && (
            <Button
              variant="link"
              size="sm"
              onClick={() => navigate(`/messages/${item.sendAttempt!.generatedMessageId}`)}
            >
              文面を開く
            </Button>
          )}
        </details>
      )}

      <Card>
        <CardHeader>
          <div className="min-w-0">
            <CardTitle className="text-body">
              From: {item.fromName ? `${item.fromName} <${item.fromEmail}>` : item.fromEmail}
            </CardTitle>
            <p className="tabular text-note text-foreground-muted">{formatDateTime(item.receivedAt)}</p>
            <p className="mt-1 text-body font-semibold">{item.subject ?? '（件名なし）'}</p>
          </div>
        </CardHeader>
        <CardContent>
          {item.bodyHtml && showImages ? (
            // HTML はメーラーと同じ表示（サニタイズ・CSP・サンドボックスの iframe）で出す。
            // 外部画像を含めて表示するのは、明示的に許可した後だけ
            <MailHtmlBody html={item.bodyHtml} allowRemoteImages />
          ) : (
            <p className="whitespace-pre-wrap text-body">
              {showQuote ? (item.bodyText ?? '') : (item.bodyStripped ?? item.bodyText ?? '')}
            </p>
          )}

          <div className="mt-3 flex flex-wrap gap-2 border-t border-border pt-3">
            {item.bodyText && item.bodyStripped !== item.bodyText && (
              <Button variant="ghost" size="sm" onClick={() => setShowQuote((prev) => !prev)}>
                {showQuote ? messages.inbox.hideQuote : messages.inbox.showQuote}
              </Button>
            )}
            {item.bodyHtml && !showImages && (
              <Button variant="ghost" size="sm" onClick={() => setShowImages(true)}>
                {messages.inbox.showImages}
              </Button>
            )}
            {item.bodyHtml && !showImages && (
              <span className="self-center text-note text-foreground-subtle">
                {messages.inbox.imagesBlocked}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <BottomActions>
        <Button
          variant="outline"
          size="lg"
          className="flex-1"
          onClick={() => {
            setReplySubject(`Re: ${item.subject ?? ''}`);
            setReplyOpen(true);
          }}
        >
          <Reply aria-hidden />
          {messages.inbox.reply}
        </Button>
        <Button
          variant="outline"
          size="lg"
          className="flex-1"
          onClick={() => setAppointmentOpen(true)}
          disabled={!item.companyId}
          disabledReason={!item.companyId ? '先に企業を紐付けてください' : undefined}
        >
          {messages.inbox.createAppointment}
        </Button>
        <Button
          size="lg"
          className="flex-1"
          onClick={() => markDone.mutate()}
          disabled={item.handlingStatus === 'done'}
        >
          <Check aria-hidden />
          {messages.inbox.markDone}
        </Button>
      </BottomActions>

      {/* 返信 */}
      <Dialog open={replyOpen} onOpenChange={setReplyOpen}>
        <DialogContent className="lg:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{messages.inbox.reply}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <p className="text-body text-foreground-muted">宛先: {item.fromEmail}</p>
            <Field label="件名" required>
              {(props) => (
                <Input {...props} value={replySubject} onChange={(e) => setReplySubject(e.target.value)} />
              )}
            </Field>
            <Field label="本文" required>
              {(props) => (
                <Textarea {...props} rows={12} value={replyBody} onChange={(e) => setReplyBody(e.target.value)} />
              )}
            </Field>
            <Button variant="outline" onClick={() => draft.mutate()} disabled={draft.isPending}>
              <Sparkles aria-hidden />
              {messages.inbox.replyDraft}
            </Button>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" size="lg" onClick={() => setReplyOpen(false)}>
              {messages.common.cancel}
            </Button>
            <Button
              size="lg"
              onClick={() => reply.mutate()}
              disabled={reply.isPending || !replySubject.trim() || !replyBody.trim()}
            >
              送信する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ClassifyDialog
        open={classifyOpen}
        onOpenChange={setClassifyOpen}
        inboundId={id}
        current={item.replyClass}
      />
      <LinkDialog open={linkOpen} onOpenChange={setLinkOpen} inboundId={id} />
      <AppointmentDialog
        open={appointmentOpen}
        onOpenChange={setAppointmentOpen}
        companyId={item.companyId}
        inboundMessageId={id}
        detectedDates={item.detectedDates}
      />
    </>
  );
};

const LinkButton = ({ inboundId, companyId }: { inboundId: string; companyId: string }) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => api.post(`/inbox/${inboundId}/link`, { companyId, applyToSameDomain: false }),
    onSuccess: () => {
      toast.success('企業に紐付けました');
      void queryClient.invalidateQueries({ queryKey: qk.inboxItem(inboundId) });
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  return (
    <Button variant="outline" size="sm" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
      選ぶ
    </Button>
  );
};

const ClassifyDialog = ({
  open,
  onOpenChange,
  inboundId,
  current,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inboundId: string;
  current: string | null;
}) => {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(current ?? 'other');

  const mutation = useMutation({
    mutationFn: () => api.post(`/inbox/${inboundId}/classify`, { replyClass: value }),
    onSuccess: () => {
      toast.success('分類を変更しました');
      void queryClient.invalidateQueries({ queryKey: qk.inboxItem(inboundId) });
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
      onOpenChange(false);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={messages.inbox.changeClass}
      summary="修正した分類は AI の品質確認に使われます。"
      detail={
        <Field label="分類" required>
          {(props) => (
            <Select {...props} value={value} onChange={(e) => setValue(e.target.value)}>
              {ReplyClasses.list.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
      }
      confirmText={messages.common.save}
      onConfirm={() => mutation.mutate()}
      isPending={mutation.isPending}
    />
  );
};

const LinkDialog = ({
  open,
  onOpenChange,
  inboundId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inboundId: string;
}) => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [applyToSameDomain, setApplyToSameDomain] = useState(true);

  const { data } = useQuery({
    queryKey: qk.companies({ q: search, limit: 20 }),
    queryFn: () =>
      api.get<{ data: { id: string; name: string; domain: string | null }[] }>('/companies', {
        q: search,
        limit: 20,
      }),
    enabled: open && search.length > 0,
  });

  const mutation = useMutation({
    mutationFn: () => api.post(`/inbox/${inboundId}/link`, { companyId, applyToSameDomain }),
    onSuccess: () => {
      toast.success('企業に紐付けました');
      void queryClient.invalidateQueries({ queryKey: qk.inboxItem(inboundId) });
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
      onOpenChange(false);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{messages.inbox.linkCompany}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Field label="企業を検索">
            {(props) => (
              <Input
                {...props}
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="社名・ドメイン"
                autoFocus
              />
            )}
          </Field>
          {data && data.data.length > 0 && (
            <ul className="max-h-64 space-y-1 overflow-y-auto">
              {data.data.map((company) => (
                <li key={company.id}>
                  <label className="flex min-h-touch cursor-pointer items-center gap-2 rounded-control px-2 hover:bg-muted">
                    <input
                      type="radio"
                      name="link-company"
                      checked={companyId === company.id}
                      onChange={() => setCompanyId(company.id)}
                      className="size-4 accent-foreground"
                    />
                    <span className="min-w-0 flex-1 truncate text-body">{company.name}</span>
                    <span className="shrink-0 text-note text-foreground-subtle">{company.domain}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          <label className="flex min-h-touch items-center gap-2 text-body">
            <input
              type="checkbox"
              className="size-4 accent-foreground"
              checked={applyToSameDomain}
              onChange={(e) => setApplyToSameDomain(e.target.checked)}
            />
            {messages.inbox.applyToSameDomain}
          </label>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button size="lg" onClick={() => mutation.mutate()} disabled={!companyId || mutation.isPending}>
            紐付ける
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
